import { type ChangeEvent, type FC, useEffect, useState } from 'react'
import { useGetEnrollmentEquityData, useSubmitEnrollmentEquityData } from '@egw/api'
import { Logger } from '@egw/logger'
import { EnrollmentLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { WEALTH_ROUTES } from '@egw/constants'
import {
  type EquitySectorAllocationInput,
  type EnrollmentEquitySectorRecommendationOutput,
} from '@egw/api/src/models/enrollment/enrollment'
import { ROUTES } from '../../../constants'
import { Equity } from '../../../components/equity/equity'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - Equity')

export const EquityPage: FC = () => {
  const { navigate } = useRouter()

  const { data, isLoading, error } = useGetEnrollmentEquityData()
  const { mutate: submit, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentEquityData()

  const equitySector = data?.enrollmentData.recommendation.investment.equitySector
  const riskTolerance = data?.enrollmentData.userInfo.investment.riskTolerance

  const [inputData, setInputData] = useState<EnrollmentEquitySectorRecommendationOutput[] | undefined>(equitySector)

  const onChangeSliders = (index: number, event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = Number(event.target.value)

    setInputData((prevState) =>
      prevState?.map((slider, i) =>
        i === index
          ? {
              ...slider,
              recommendedRange: {
                ...slider.recommendedRange,
                recommendedPercent: newValue,
              },
            }
          : slider,
      ),
    )
  }

  const handleContinueClick = async (): Promise<void> => {
    const equitySectorAllocation = inputData?.map((item) => ({
      equitySector: item.type,
      allocationPercent: item.recommendedRange.recommendedPercent,
    }))

    try {
      const response = await submit({
        investmentProfile: {
          equitySectorAllocation: equitySectorAllocation as EquitySectorAllocationInput[],
        },
      })
      if (response?.path) {
        navigate(response.path)
      }
    } catch (err) {
      log.error('Error submitting enrollment intro data', err)
    }
  }

  useValidateRoute(data?.path)

  useEffect(() => {
    if (equitySector) {
      setInputData(equitySector)
    }
  }, [equitySector])

  return (
    <EnrollmentLayout className="bg-background-light" title="Equity">
      <Equity
        riskTolerance={riskTolerance}
        sliderData={inputData}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
        error={error}
        submitError={submitError}
        onChangeSliders={onChangeSliders}
        handleContinueClick={() => void handleContinueClick()}
      />
    </EnrollmentLayout>
  )
}
